/**
 * Post-submit: photograph the sheets the student actually wrote on.
 *
 * Unlike the paper check, the number of sheets is not known in advance — the
 * student may have filled one side or six. So this collects photos one at a
 * time and lets them say when they are finished, with a minimum of one.
 */
import { useCallback, useEffect, useState } from "react";
import { Camera, Check, RotateCcw } from "lucide-react";
import { PhoneButton } from "./PhoneButton";

export function AnswerSheetStep({
  capture,
  upload,
  uploading,
  uploadedCount,
}: {
  capture: () => Promise<Blob | null>;
  upload: (blob: Blob) => Promise<boolean>;
  uploading: boolean;
  uploadedCount: number;
}) {
  const [preview, setPreview] = useState<{ blob: Blob; url: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const take = useCallback(async () => {
    setError(null);
    const blob = await capture();
    if (!blob) {
      setError("Could not take the photo. Try again.");
      return;
    }
    setPreview({ blob, url: URL.createObjectURL(blob) });
  }, [capture]);

  const accept = useCallback(async () => {
    if (!preview) return;
    const ok = await upload(preview.blob);
    if (!ok) {
      setError("Could not upload. Check your connection and try again.");
      return;
    }
    URL.revokeObjectURL(preview.url);
    setPreview(null);
  }, [preview, upload]);

  if (finished) {
    return (
      <div className="flex flex-col gap-3">
        <p className="flex items-center gap-2 text-sm font-medium text-answered">
          <Check className="size-4" />
          <span className="tabular">{uploadedCount}</span> uploaded
        </p>
        <p className="text-sm text-ink-muted">
          You can put the phone away now.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-ink">
          Photograph your answer sheets
        </h1>
        <p className="mt-0.5 text-xs text-ink-muted">
          Every sheet you wrote on, front and back.
        </p>
      </div>

      <p className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink">
        <span className="tabular font-medium">{uploadedCount}</span> uploaded so
        far
      </p>

      {preview ? (
        <>
          <div className="overflow-hidden rounded-md border border-border bg-surface">
            <img src={preview.url} alt="Preview" className="block w-full" />
          </div>

          <p className="text-sm font-medium text-ink">
            Can you read your own writing?
          </p>

          {error ? <ErrorLine message={error} /> : null}

          <div className="flex flex-col gap-2">
            <PhoneButton
              icon={<Check />}
              onClick={() => void accept()}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : "Yes, keep it"}
            </PhoneButton>
            <PhoneButton
              variant="secondary"
              icon={<RotateCcw />}
              onClick={() => {
                URL.revokeObjectURL(preview.url);
                setPreview(null);
              }}
              disabled={uploading}
            >
              Retake
            </PhoneButton>
          </div>
        </>
      ) : (
        <>
          {error ? <ErrorLine message={error} /> : null}

          <PhoneButton icon={<Camera />} onClick={() => void take()}>
            {uploadedCount === 0 ? "Take photo" : "Take another photo"}
          </PhoneButton>

          <PhoneButton
            variant="secondary"
            onClick={() => setFinished(true)}
            disabled={uploadedCount === 0}
          >
            {uploadedCount === 0
              ? "Take at least one photo"
              : "I have photographed everything"}
          </PhoneButton>
        </>
      )}
    </div>
  );
}

function ErrorLine({ message }: { message: string }) {
  return (
    <p className="rounded-md border border-violation/30 bg-violation-soft px-3 py-2 text-xs text-violation">
      {message}
    </p>
  );
}
